import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import { fromLonLat } from "ol/proj";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import OSM from "ol/source/OSM";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Style from "ol/style/Style";
import Text from "ol/style/Text";
import CircleStyle from "ol/style/Circle";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import { trackEvent } from "@/lib/analytics";

const TargetMapOverlay = ({
  group,
  addresses = [],
  selectedAddressId,
  onSelectAddress,
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const vectorLayerRef = useRef(null);
  const fittedAddressesRef = useRef(null);
  const listContainerRef = useRef(null);
  const selectedItemRef = useRef(null);
  const [lineSegments, setLineSegments] = useState([]);
  const [mapSize, setMapSize] = useState([0, 0]);
  const debugLoggedRef = useRef(false);
  const retryRenderRef = useRef(false);
  const isDev = import.meta.env?.DEV;
  const [mapReady, setMapReady] = useState(false);

  const getAddressEmojiText = (address) => {
    if (
      address.id === selectedAddressId &&
      group?.target_selected_address_emoji &&
      !address.has_paid_orders
    ) {
      return group.target_selected_address_emoji;
    }
    const specialEmojis = Array.isArray(address.ordered_special_item_emojis)
      ? address.ordered_special_item_emojis.filter(Boolean)
      : [];
    if (specialEmojis.length > 0) {
      const visible = specialEmojis.slice(0, 4).join(" ");
      return specialEmojis.length > 4 ? `${visible} +${specialEmojis.length - 4}` : visible;
    }
    if (
      group?.show_only_special_price_items &&
      group?.target_special_price_wait_emoji &&
      !address.has_paid_orders &&
      !address.is_ready
    ) {
      return group.target_special_price_wait_emoji;
    }
    if (group?.complete_address_when_all_special_items_ordered) {
      return address.has_paid_orders ? address.emoji_ready : address.emoji_wait;
    }
    return address.has_paid_orders || address.is_ready ? address.emoji_ready : address.emoji_wait;
  };

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({ source: vectorSource });
    vectorLayerRef.current = vectorLayer;

    const map = new Map({
      target: container,
      layers: [
        new TileLayer({ source: new OSM({ crossOrigin: "anonymous" }) }),
        vectorLayer
      ],
      view: new View({
        center: fromLonLat([37.618423, 55.751244]),
        zoom: 5
      })
    });

    map.on("singleclick", (evt) => {
      map.forEachFeatureAtPixel(evt.pixel, (feature) => {
        const addressId = feature.get("addressId");
        const disabled = feature.get("disabled");
        if (addressId && !disabled && onSelectAddress) {
          onSelectAddress(addressId);
          trackEvent(`targetmap_${group?.code || 'unknown'}_select_address`, {
            code: group?.code,
            address_id: addressId,
            source: 'map',
          });
        }
      });
    });

    mapInstanceRef.current = map;
    setMapReady(true);

    const refreshMapSize = () => {
      requestAnimationFrame(() => map.updateSize());
    };
    refreshMapSize();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(refreshMapSize);
      resizeObserver.observe(container);
    }
    window.addEventListener("resize", refreshMapSize);
    window.addEventListener("orientationchange", refreshMapSize);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", refreshMapSize);
      window.removeEventListener("orientationchange", refreshMapSize);
      map.setTarget(null);
      mapInstanceRef.current = null;
      vectorLayerRef.current = null;
      setMapReady(false);
    };
  }, [onSelectAddress]);

  const buildLineSegments = (map, points) => {
    if (!map || !Array.isArray(points) || points.length === 0) {
      return [];
    }
    const byId = new Map(points.map((address) => [address.id, address]));
    const segments = [];
    const debugSkips = [];
    points.forEach((address) => {
      if (!address.linked_address_id) {
        if (isDev) debugSkips.push({ id: address.id, reason: 'no_link' });
        return;
      }
      const linked = byId.get(address.linked_address_id);
      if (!linked) {
        if (isDev) debugSkips.push({ id: address.id, reason: 'linked_not_found', linkedId: address.linked_address_id });
        return;
      }
      const lng1 = Number(address.longitude);
      const lat1 = Number(address.latitude);
      const lng2 = Number(linked.longitude);
      const lat2 = Number(linked.latitude);
      if (![lng1, lat1, lng2, lat2].every(Number.isFinite)) {
        if (isDev) debugSkips.push({ id: address.id, reason: 'invalid_coords', coords: { lng1, lat1, lng2, lat2 } });
        return;
      }
      const pixel1 = map.getPixelFromCoordinate(fromLonLat([lng1, lat1]));
      const pixel2 = map.getPixelFromCoordinate(fromLonLat([lng2, lat2]));
      if (!pixel1 || !pixel2) {
        if (isDev) debugSkips.push({ id: address.id, reason: 'invalid_pixels', pixel1, pixel2 });
        return;
      }

      const hasOrdersA = Boolean(address.has_paid_orders);
      const hasOrdersB = Boolean(linked.has_paid_orders);
      if (!hasOrdersA && !hasOrdersB && address.link_skip_none) return;
      if ((hasOrdersA || hasOrdersB) && !(hasOrdersA && hasOrdersB) && address.link_skip_one) return;
      if (hasOrdersA && hasOrdersB && address.link_skip_both) return;
      let stroke = address.link_color_none || "#94a3b8";
      if (hasOrdersA && hasOrdersB) {
        stroke = address.link_color_both || address.link_color_one || stroke;
      } else if (hasOrdersA || hasOrdersB) {
        stroke = address.link_color_one || stroke;
      }

      segments.push({
        id: `${address.id}-${linked.id}`,
        x1: pixel1[0],
        y1: pixel1[1],
        x2: pixel2[0],
        y2: pixel2[1],
        stroke
      });
    });
    if (isDev) {
      (window.__TARGETMAP_DEBUG = window.__TARGETMAP_DEBUG || {});
      window.__TARGETMAP_DEBUG.lineDebug = { segments, skips: debugSkips };
    }
    return segments;
  };

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    requestAnimationFrame(() => {
      map.updateSize();
    });

    let frameId = null;
    const updateLines = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        const size = map.getSize();
        const targetEl = map.getTargetElement();
        const fallbackSize = targetEl
          ? [targetEl.clientWidth, targetEl.clientHeight]
          : size;
        const nextSize =
          Array.isArray(size) && size[0] > 0 && size[1] > 0 ? size : fallbackSize;
        if (Array.isArray(nextSize)) {
          setMapSize(nextSize);
        }
        let segments = buildLineSegments(map, addresses);
        const linkedCount = addresses.filter((address) => address.linked_address_id).length;
        if (segments.length < linkedCount) {
          // Fallback: build segments with minimal guards for all links.
          segments = [];
          addresses.forEach((address) => {
            if (!address.linked_address_id) return;
            const linked = addresses.find((item) => item.id === address.linked_address_id);
            if (!linked) return;
            const lng1 = Number(address.longitude);
            const lat1 = Number(address.latitude);
            const lng2 = Number(linked.longitude);
            const lat2 = Number(linked.latitude);
            const pixel1 = map.getPixelFromCoordinate(fromLonLat([lng1, lat1]));
            const pixel2 = map.getPixelFromCoordinate(fromLonLat([lng2, lat2]));
            if (!pixel1 || !pixel2) return;
            const hasOrdersA = Boolean(address.has_paid_orders);
            const hasOrdersB = Boolean(linked.has_paid_orders);
            if (!hasOrdersA && !hasOrdersB && address.link_skip_none) return;
            if ((hasOrdersA || hasOrdersB) && !(hasOrdersA && hasOrdersB) && address.link_skip_one) return;
            if (hasOrdersA && hasOrdersB && address.link_skip_both) return;
            let stroke = address.link_color_none || "#94a3b8";
            if (hasOrdersA && hasOrdersB) {
              stroke = address.link_color_both || address.link_color_one || stroke;
            } else if (hasOrdersA || hasOrdersB) {
              stroke = address.link_color_one || stroke;
            }
            segments.push({
              id: `${address.id}-${linked.id}-fallback`,
              x1: pixel1[0],
              y1: pixel1[1],
              x2: pixel2[0],
              y2: pixel2[1],
              stroke
            });
          });
        }
        setLineSegments(segments);
        if (segments.length === 0 && !retryRenderRef.current && linkedCount > 0) {
          retryRenderRef.current = true;
          map.once("rendercomplete", updateLines);
          map.render();
        }
        if (isDev && !debugLoggedRef.current) {
          debugLoggedRef.current = true;
          let linkedFoundCount = 0;
          let coordsValidCount = 0;
          let pixelValidCount = 0;
          addresses.forEach((address) => {
            if (!address.linked_address_id) return;
            const linked = addresses.find((item) => item.id === address.linked_address_id);
            if (!linked) return;
            linkedFoundCount += 1;
            const lng1 = Number(address.longitude);
            const lat1 = Number(address.latitude);
            const lng2 = Number(linked.longitude);
            const lat2 = Number(linked.latitude);
            if (![lng1, lat1, lng2, lat2].every(Number.isFinite)) return;
            coordsValidCount += 1;
            const pixel1 = map.getPixelFromCoordinate(fromLonLat([lng1, lat1]));
            const pixel2 = map.getPixelFromCoordinate(fromLonLat([lng2, lat2]));
            if (!pixel1 || !pixel2) return;
            pixelValidCount += 1;
          });
          const sample = addresses.find((address) => address.linked_address_id);
          const linked = sample
            ? addresses.find((address) => address.id === sample.linked_address_id)
            : null;
          const samplePixels = sample && linked
            ? {
                pixel1: map.getPixelFromCoordinate(fromLonLat([
                  Number(sample.longitude),
                  Number(sample.latitude)
                ])),
                pixel2: map.getPixelFromCoordinate(fromLonLat([
                  Number(linked.longitude),
                  Number(linked.latitude)
                ]))
              }
            : null;
          console.log("[TargetMapOverlay] debug", {
            mapSize: nextSize,
            addressCount: addresses.length,
            segmentsCount: segments.length,
            linkedCount,
            linkedFoundCount,
            coordsValidCount,
            pixelValidCount,
            samplePixels,
            lineDebug: window.__TARGETMAP_DEBUG?.lineDebug
          });
        }
      });
    };

    updateLines();
    map.once("rendercomplete", updateLines);
    map.on("moveend", updateLines);
    map.on("change:size", updateLines);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      map.un("moveend", updateLines);
      map.un("change:size", updateLines);
    };
  }, [addresses, mapReady]);

  useEffect(() => {
    const layer = vectorLayerRef.current;
    const map = mapInstanceRef.current;
    if (!layer || !map) return;

    const source = layer.getSource();
    source.clear();

    if (!addresses.length) {
      return;
    }

    const features = addresses.map((address) => {
      const feature = new Feature({
        geometry: new Point(fromLonLat([
          Number(address.longitude),
          Number(address.latitude)
        ])),
        addressId: address.id,
        disabled: Boolean(address.has_paid_orders)
      });

      const disabled = Boolean(address.has_paid_orders);
      feature.setStyle(new Style({
        text: new Text({
          text: getAddressEmojiText(address),
          font: "26px sans-serif",
          offsetY: -10,
          overflow: true
        }),
        image: new CircleStyle({
          radius: address.id === selectedAddressId ? 11 : 7,
          fill: new Fill({
            color: disabled
              ? "rgba(100,116,139,0.85)"
              : address.id === selectedAddressId
              ? "rgba(34,197,94,0.8)"
              : "rgba(59,130,246,0.8)"
          }),
          stroke: new Stroke({ color: "#ffffff", width: 2 })
        })
      }));
      return feature;
    });

    source.addFeatures(features);
    if (fittedAddressesRef.current !== addresses) {
      fittedAddressesRef.current = addresses;
      const extent = source.getExtent();
      if (extent && extent.every((val) => Number.isFinite(val))) {
        map.getView().fit(extent, { padding: [40, 40, 40, 40], maxZoom: 13 });
      }
    }
  }, [addresses, selectedAddressId, mapReady]);

  useEffect(() => {
    const node = selectedItemRef.current;
    const container = listContainerRef.current;
    if (!node || !container) return;
    const nodeTop = node.offsetTop;
    const nodeBottom = nodeTop + node.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;
    if (nodeTop < viewTop || nodeBottom > viewBottom) {
      container.scrollTo({
        top: nodeTop - (container.clientHeight - node.offsetHeight) / 2,
        behavior: 'smooth',
      });
    }
  }, [selectedAddressId]);

  return (
    <div
      className="w-full bg-white border rounded-xl shadow-sm p-4 space-y-3"
      data-testid="target-map-overlay"
    >
      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full h-[320px] md:flex-1 md:h-[420px] rounded-lg overflow-hidden border">
          <div className="relative w-full h-full touch-pan-y">
            <div ref={mapContainerRef} className="w-full h-full z-0" />
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none z-10"
              aria-hidden="true"
              width={mapSize[0]}
              height={mapSize[1]}
              viewBox={`0 0 ${mapSize[0]} ${mapSize[1]}`}
            >
              {lineSegments.map((segment) => (
                <line
                  key={segment.id}
                  x1={segment.x1}
                  y1={segment.y1}
                  x2={segment.x2}
                  y2={segment.y2}
                  stroke={segment.stroke}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              ))}
            </svg>
          </div>
        </div>
        <div className="w-full md:w-80 space-y-3" data-testid="target-map-list">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">
              Целевая группа
            </p>
            <p className="text-base font-semibold text-slate-900">
              {group?.name || "Target Map"}
            </p>
            <p className="text-sm text-slate-500">
              {addresses.length} адресов · код {group?.code || "-"}
            </p>
          </div>
          <div ref={listContainerRef} className="relative space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {addresses.map((address) => (
              <button
                key={address.id}
                ref={address.id === selectedAddressId ? selectedItemRef : null}
                type="button"
                onClick={() => {
                  if (!address.has_paid_orders) {
                    onSelectAddress?.(address.id);
                    trackEvent(`targetmap_${group?.code || 'unknown'}_select_address`, {
                      code: group?.code,
                      address_id: address.id,
                      source: 'list',
                    });
                  }
                }}
                disabled={Boolean(address.has_paid_orders)}
                className={`w-full text-left border rounded-lg p-3 transition ${
                  address.id === selectedAddressId
                    ? "border-amber-400 bg-amber-50"
                    : address.has_paid_orders
                      ? "border-slate-200 bg-slate-100 opacity-70 cursor-not-allowed"
                    : "border-slate-200 hover:border-amber-200 hover:bg-amber-50/50"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-slate-900">{address.name}</p>
                  <span className="text-lg inline-flex flex-wrap justify-end gap-1 max-w-24">
                    {getAddressEmojiText(address)}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {Number(address.latitude).toFixed(3)}, {Number(address.longitude).toFixed(3)}
                </p>
                {address.has_paid_orders && (
                  <p className="text-xs font-medium text-slate-600 mt-1">
                    Все спец-товары заказаны
                  </p>
                )}
              </button>
            ))}
          </div>
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="target-map-delivery-lock">
            После выбора точки оформление доставки будет заблокировано и привязано к выбранной геометке.
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetMapOverlay;
